import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Card, CardActionArea, CardContent, CircularProgress, Alert } from '@mui/material';
import { Store as VenueIcon } from '@mui/icons-material';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import venueService from '../../services/venueService';
import type { Venue } from '../../types/venue';

/**
 * Venue picker — landing page for VENUE_USER with multiple assigned venues.
 *
 * Behavior:
 *   - VENUE_USER, ≥2 assigned venues → list of cards (this page).
 *   - VENUE_USER, 1 assigned venue   → auto-redirect to that venue (login
 *     normally handles this, but if the user lands here directly we still
 *     do the right thing).
 *   - VENUE_USER, 0 assigned venues  → /unauthorized (data is misconfigured).
 *   - SYSTEM_ADMIN / ADMIN          → redirect to /admin. They already have
 *     the full venues list under "Inventory > Venues"; the picker is purely
 *     a venue-user UX. Showing it to admins would create two ways to do the
 *     same thing — not useful.
 */
const MyVenues: React.FC = () => {
  const { user, isVenueUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [venues, setVenues] = useState<Venue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Backend already filters /admin/venue by assignedVenueIds for VENUE_USER,
    // so we just hit the same list endpoint and trust the server-side scope.
    let cancelled = false;
    setLoading(true);
    venueService
      .getAllVenues(1, 200, 'name', 'asc', undefined, undefined, 'all')
      .then((res) => {
        if (!cancelled) setVenues(res.data);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load your venues. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Admins land somewhere more useful than a venue-user picker.
  if (isAdmin()) return <Navigate to="/admin" replace />;

  // Venue users with no assignments shouldn't be here.
  if (isVenueUser() && (user?.assignedVenueIds ?? []).length === 0) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Venue users with exactly one assignment skip the picker.
  if (isVenueUser() && (user?.assignedVenueIds ?? []).length === 1) {
    return <Navigate to={`/admin/venues/${user!.assignedVenueIds![0]}`} replace />;
  }

  return (
    <Box sx={{ p: 3, maxWidth: 960, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Choose a venue
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Welcome{user?.firstName ? `, ${user.firstName}` : ''}. Select the venue you want to work in.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : venues.length === 0 ? (
          <Typography variant="body1" color="text.secondary">
            You have no venues assigned. Contact your administrator.
          </Typography>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
              },
            }}
          >
            {venues.map((v) => (
              <Card
                key={v._id}
                sx={{
                  borderRadius: 2,
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 18px rgba(0, 0, 0, 0.12)',
                  },
                }}
              >
                <CardActionArea onClick={() => navigate(`/admin/venues/${v._id}`)} sx={{ p: 0 }}>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
                    <VenueIcon fontSize="large" color="primary" />
                    <Box>
                      <Typography variant="h6">{v.name}</Typography>
                      {v.city && (
                        <Typography variant="body2" color="text.secondary">
                          {v.city}{v.country ? `, ${v.country}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default MyVenues;
